import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Mail, Phone, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ContactDialog } from "@/components/crm/ContactDialog";
import { RecordDrawer } from "@/components/crm/RecordDrawer";
import { SalesShell } from "@/components/crm/SalesShell";
import { BulkBar, SelectBox, useRowSelection } from "@/components/crm/BulkBar";
import { SalesActions } from "@/components/crm/SalesActions";
import { EmptyState, SelectField } from "@/components/crm/parts";
import { useAccountContacts, useAccounts, useContacts, useDeleteRecord } from "@/lib/crm/useCrm";
import { contactName, type Contact } from "@/lib/crm/types";
import { contactSheets } from "@/lib/exports/crm";
import { contactVisual } from "@/lib/crm/visual-sources";
import { useSalesOwner } from "@/lib/crm/owner-filter";
import { useOrgMembers } from "@/lib/crm/useOrgMembers";

export const Route = createFileRoute("/_authenticated/sales/contacts")({
  head: () => ({
    meta: [
      { title: "Contacts — MyPriorities" },
      {
        name: "description",
        content:
          "Contact directory with decision makers, budget holders, commercial and operational contacts per account.",
      },
      { property: "og:title", content: "Contacts — MyPriorities" },
      {
        property: "og:description",
        content: "Know who decides, who pays and who runs the day job.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: ContactsPage,
});

function ContactsPage() {
  return (
    <SalesShell
      section="customers"
      title="Contacts"
      subtitle="Who decides, who holds budget, who runs delivery — and how strong the relationship is."
    >

      {({ orgId, userId }) => <Contacts orgId={orgId} userId={userId} />}
    </SalesShell>
  );
}

function Contacts({ orgId, userId }: { orgId: string; userId: string }) {
  const contacts = useContacts(orgId);
  const accounts = useAccounts(orgId);
  const links = useAccountContacts(orgId);
  const remove = useDeleteRecord("crm_contacts");
  const selection = useRowSelection();

  const [search, setSearch] = useState("");
  const [accountFilter, setAccountFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Contact | null>(null);
  const [detail, setDetail] = useState<Contact | null>(null);

  const { scope } = useSalesOwner();
  const { ownerLabel } = useOrgMembers(orgId);
  const accountRows = accounts.data ?? [];
  const linkRows = links.data ?? [];
  const accountIdFor = (contactId: string) =>
    linkRows.find((l) => l.contact_id === contactId)?.account_id ?? null;
  const accountNameFor = (contactId: string) => {
    const id = accountIdFor(contactId);
    return accountRows.find((a) => a.id === id)?.name ?? "No account";
  };

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return scope(contacts.data ?? []).filter((c) => {
      if (accountFilter !== "all" && accountIdFor(c.id) !== accountFilter) return false;
      if (!q) return true;
      return (
        contactName(c).toLowerCase().includes(q) ||
        (c.email ?? "").toLowerCase().includes(q) ||
        (c.job_title ?? "").toLowerCase().includes(q)
      );
    });
  }, [contacts.data, search, accountFilter, linkRows]);

  const tags = (c: Contact) =>
    [
      c.decision_maker ? "Decision maker" : null,
      c.budget_holder ? "Budget holder" : null,
      c.commercial_contact ? "Commercial" : null,
      c.operational_contact ? "Operational" : null,
    ].filter((v): v is string => Boolean(v));

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-0 flex-1 basis-full sm:basis-56">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search contacts"
          />
        </div>
        <div className="min-w-0 basis-full sm:basis-56">
          <SelectField
            label="Account"
            value={accountFilter}
            onChange={setAccountFilter}
            options={[
              { value: "all", label: "All accounts" },
              ...accountRows.map((a) => ({ value: a.id, label: a.name })),
            ]}
          />
        </div>
        <div className="flex w-full min-w-0 flex-wrap items-center gap-2 sm:w-auto sm:shrink-0">
          <SalesActions
            exports={[
              {
                label: "Contacts",
                baseName: "contacts",
                sheets: () => contactSheets({ contacts: rows, accountNameFor, ownerLabel }),
              },
            ]}
            visual={() => contactVisual(rows, accountNameFor)}
            sourceNote="From your contact list"
          />
          <Button
            className="shrink-0"
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
          >
            <Plus className="size-4" /> New contact
          </Button>
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyState
          title="No contacts yet"
          body="Add the people you deal with and mark who decides, who pays and who runs delivery."
        />
      ) : (
        <>
        <BulkBar
          table="crm_contacts"
          noun="contact"
          ids={rows.map((r) => r.id)}
          selected={selection.selected}
          onReplace={selection.replace}
          onClear={selection.clear}
          blockedHint="They are still attached to accounts or opportunities."
        />
        <ul className="grid gap-2">
          {rows.map((c) => (
            <li key={c.id} className="rounded-lg border border-border bg-card p-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="flex min-w-0 items-start gap-3">
                  <SelectBox
                    checked={selection.isSelected(c.id)}
                    onChange={() => selection.toggle(c.id)}
                    label={`Select ${contactName(c)}`}
                  />
                <div className="min-w-0">
                  <button
                    type="button"
                    className="text-left text-sm font-semibold hover:underline"
                    onClick={() => setDetail(c)}
                  >
                    {contactName(c)}
                  </button>
                  <p className="text-[11px] text-muted-foreground">
                    {c.job_title || "Role not set"} · {accountNameFor(c.id)}
                    {c.department ? ` · ${c.department}` : ""}
                  </p>
                  <p className="mt-1 flex flex-wrap gap-3 text-[11px] text-muted-foreground">
                    {c.email ? (
                      <span className="inline-flex items-center gap-1">
                        <Mail className="size-3" /> {c.email}
                      </span>
                    ) : null}
                    {c.phone ? (
                      <span className="inline-flex items-center gap-1">
                        <Phone className="size-3" /> {c.phone}
                      </span>
                    ) : null}
                  </p>
                </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {tags(c).map((t) => (
                    <Badge key={t} variant="secondary">
                      {t}
                    </Badge>
                  ))}
                  <Badge variant="outline">{c.relationship_strength ?? "Unknown"}</Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditing(c);
                      setDialogOpen(true);
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Delete contact"
                    onClick={() =>
                      void remove
                        .mutateAsync(c.id)
                        .then(() => toast.success("Contact deleted"))
                        .catch(() => toast.error("Could not delete that contact"))
                    }
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
        </>
      )}

      <ContactDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        orgId={orgId}
        userId={userId}
        contact={editing}
        accounts={accountRows}
        linkedAccountId={editing ? accountIdFor(editing.id) : null}
        defaultAccountId={accountFilter === "all" ? null : accountFilter}
      />

      <RecordDrawer
        open={Boolean(detail)}
        onOpenChange={(v) => !v && setDetail(null)}
        orgId={orgId}
        userId={userId}
        kind="contact"
        recordId={detail?.id ?? null}
        title={detail ? contactName(detail) : ""}
        subtitle={detail ? accountNameFor(detail.id) : undefined}
      />
    </div>
  );
}
